import React, { useCallback, useEffect, useState } from 'react';
import { Box, useBreakpointValue, useToast } from '@chakra-ui/react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { WhatsappConversation, WhatsappNumber } from '../../../utils/types';
import { getWAConversations, getWANumber } from '../../../hooks/whatsapp';
import ChatSidebar from '../pages/Conversations';
import {
  WhatsAppInboxShell,
  WhatsAppSidebarShell,
  WhatsAppThreadPlaceholder,
} from './whatsappInboxUi';

const normalizeConversations = (data: unknown): WhatsappConversation[] => {
  if (Array.isArray(data)) {
    return data as WhatsappConversation[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: WhatsappConversation[] }).results;
  }

  return [];
};

const normalizeWANumber = (data: unknown): WhatsappNumber | null => {
  if (Array.isArray(data)) {
    return (data[0] as WhatsappNumber) || null;
  }

  if (data && typeof data === 'object' && 'phone_number' in (data as Record<string, unknown>)) {
    return data as WhatsappNumber;
  }

  return null;
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { orgId, convId } = useParams();
  const [whatsappNumber, setWhatsappNumber] = useState<WhatsappNumber | null>(null);
  const [loadingNumber, setLoadingNumber] = useState(true);
  const [numberError, setNumberError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<WhatsappConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const location = useLocation();

  const isConversationPage = Boolean(
    matchPath('/organization/:orgId/inbox/whatsapp/conversations/:convId', location.pathname),
  );

  const fetchWhatsappNumber = useCallback(async () => {
    if (!orgId) {
      return;
    }

    setLoadingNumber(true);
    try {
      const res = await getWANumber(orgId);
      if (!res.success) {
        setNumberError('Unable to load WhatsApp account details.');
        setWhatsappNumber(null);
      } else {
        setWhatsappNumber(normalizeWANumber(res.data));
        setNumberError(null);
      }
    } catch {
      setNumberError('Unable to load WhatsApp account details.');
      setWhatsappNumber(null);
      toast({
        title: 'Could not load WhatsApp account',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingNumber(false);
    }
  }, [orgId, toast]);

  const fetchConversations = useCallback(async () => {
    if (!orgId) {
      return;
    }

    setLoadingConversations(true);
    try {
      const res = await getWAConversations(orgId);
      if (res.success) {
        setConversations(normalizeConversations(res.data));
        setConversationError(null);
      } else {
        setConversations([]);
        setConversationError('Unable to load conversations.');
      }
    } catch {
      setConversations([]);
      setConversationError('Unable to load conversations.');
    } finally {
      setLoadingConversations(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchWhatsappNumber();
    fetchConversations();
  }, [fetchConversations, fetchWhatsappNumber]);

  useEffect(() => {
    if (!orgId) {
      return;
    }

    const interval = window.setInterval(() => {
      fetchConversations();
    }, 15000);

    return () => window.clearInterval(interval);
  }, [fetchConversations, orgId]);

  const threadContent = !isMobile && !isConversationPage ? (
    <WhatsAppThreadPlaceholder
      title="Select a chat to start messaging"
      description="Use the inbox to review conversation history, stay inside the 24-hour messaging window, and respond without leaving the CRM."
    />
  ) : (
    <Box flex={1} minW={0} h="100%" bg="#F0F2F5">
      {children}
    </Box>
  );

  return (
    <WhatsAppInboxShell
      sidebar={
        (!isMobile || !isConversationPage) && orgId ? (
          <WhatsAppSidebarShell>
            <ChatSidebar
              orgId={orgId}
              whatsappNumber={whatsappNumber}
              conversations={conversations}
              selectedConversationId={convId}
              isLoading={loadingConversations || loadingNumber}
              isError={Boolean(numberError || conversationError)}
              errorMessage={numberError || conversationError || undefined}
              onRefresh={fetchConversations}
              onOpenSettings={() => navigate(`/organization/${orgId}/inbox/whatsapp/settings`)}
            />
          </WhatsAppSidebarShell>
        ) : null
      }
      thread={threadContent}
    />
  );
};

export default Layout;
